import { ModalContextProps, useModalContext } from '@adsk/napi-shared'
import { BROADCAST_MESSAGES, useBroadcastChannel } from '@adsk/napi-shared'
import {
	OnboardingUserAppContextType,
	useOnboardingUserContext,
} from '../../../store'
import { Flex } from '@adsk/alloy-react-box'
import Button, { LinkButton } from '@adsk/alloy-react-button'
import { ONBOARDED_SOURCES } from '../../../constants'
import React from 'react'
import {
	type SaveBNUserDto,
	type CreateBuilderNetworkUserResult,
	NapiClient,
	OnboardedBuilderNetworkResponse,
} from '@adsk/napi-api-client'
import _ from 'lodash'
import { showSubmitButtonLoader } from '../../../store/actions'
import { track } from '@adsk/acs-web-analytics'
import ANALYTICS_TYPES from '../../../types/AnalyticsTypes'
import { getPackageInfo } from '../../../utils'
import { BugsnagNotify } from '@adsk/napi-bugsnag'
import { useTranslation } from '../../../../i18n'
import { ONBOARDING_FLOW } from '../../../types/ActionTypes'
import { startPendoGuides } from '../../../utils/pendo'
import { useUser, EntitlementsUser } from '@adsk/alloy-provider'

const FooterComponent = (): JSX.Element => {
	const { version, name } = getPackageInfo()
	const { t } = useTranslation()
	const { closeModal } = useModalContext() as ModalContextProps
	const { setUser, user } = useUser()
	const {
		state: {
			marketingCompanyType,
			displaySubmitButtonLoader,
			submitState,
			userInfo,
			flowType,
			config,
		},
		dispatch,
		externalProps,
	} = useOnboardingUserContext() as OnboardingUserAppContextType

	const { postMessage } = useBroadcastChannel(
		BROADCAST_MESSAGES.USER_CLOSE_ONBOARDING_MODAL
	)

	const updateAccUser = (response: CreateBuilderNetworkUserResult) => {
		try {
			if (setUser) {
				setUser({
					...user,
					name: `${response?.firstName?.trim()} ${response?.lastName?.trim()}`,
					firstName: response?.firstName?.trim(),
					lastName: response?.lastName?.trim(),
					imageUrl: response?.avatarUrl,
				} as unknown as EntitlementsUser)
			}
		} catch (e) {
			BugsnagNotify(e as Error, {
				message:
					'[User Onboarding] Error updating user information in the acc via setUser hook',
			})
		}
	}

	const saveHandler = () => {
		dispatch(showSubmitButtonLoader(true))
		const { emailAddresses, companyName, companyType, ...rest } =
			userInfo as unknown as OnboardedBuilderNetworkResponse
		const jobTitle = userInfo?.title?.trim()
		const selectedMarketingCompanyType = Object.keys(
			marketingCompanyType
		).filter((value) => marketingCompanyType[value]) as string[]
		NapiClient.saveUserToBuildersNetwork({
			..._.omit(rest, ['updatedAvatarUrl']),
			firstName: userInfo?.firstName?.trim(),
			lastName: userInfo?.lastName?.trim(),
			avatarUrl: userInfo?.updatedAvatarUrl,
			title: jobTitle,
			companyName: companyName?.trim(),
			companyType: selectedMarketingCompanyType,
			onboardedSource: ONBOARDED_SOURCES.ACC,
		} as unknown as SaveBNUserDto)
			.then((response: CreateBuilderNetworkUserResult) => {
				externalProps?.setShowModal?.(false)
				track(ANALYTICS_TYPES.NAPI_USER_ONBOARDED, {
					package: name,
					version,
					flowType: flowType,
				})
				if (flowType === ONBOARDING_FLOW) {
					externalProps?.onSuccess?.()
				}
				updateAccUser(response)
				closeModal()
				startPendoGuides()
				postMessage(BROADCAST_MESSAGES.USER_CLOSE_ONBOARDING_MODAL)
			})
			.catch((error: Error) => {
				BugsnagNotify(error, {
					message:
						'Network Save User to Builders Network API Error on Profile Edit Confirmation component',
				})
				track(ANALYTICS_TYPES.NAPI_USER_ONBOARDING_FAILED, {
					package: name,
					version,
				})
				closeModal()
			})
			.finally(() => {
				dispatch(showSubmitButtonLoader(false))
			})
	}

	return (
		<Flex justifyContent={'space-between'} alignItems={'center'} width={'100%'}>
			<LinkButton
				style={{ order: 1 }}
				data-testid={'learnMoreTestId'}
				onClick={() => {
					track(ANALYTICS_TYPES.NAPI_USERS_CLICKED_STATIC_LINK, {
						component: name,
						version,
						linkText: 'learn more',
						link: config?.LEARN_MORE,
					})
					window.open(config?.LEARN_MORE, '_blank')
				}}
				size={LinkButton.SIZES.SMALL}
			>
				{t('LEARN_MORE')}
			</LinkButton>
			<Button
				data-testid={'saveButtonTestId'}
				disabled={!submitState}
				style={{ order: 2 }}
				variant={Button.VARIANTS.PRIMARY}
				onClick={saveHandler}
				loading={displaySubmitButtonLoader}
			>
				{t('SAVE')}
			</Button>
		</Flex>
	)
}

export default FooterComponent
